import {
  addCommasToNumbers,
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../../../utilities/numberFormatters';
import type {
  EmirFundRow,
  EmirFundSectionMetrics,
  EmirStandardBreakdownPayload,
} from '../../../../../../api/rs-hg/emir';
import type {
  EmirAssetLevelTableRow,
  EmirFundLevelTableRow,
  EmirMancoLevelTableRow,
} from './emirFundSummary.types';

export function formatEmirSectionTitle(selectedKey: string): string {
  const title = selectedKey.replace('derivatives', '');
  const words = title
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.substring(1));
  return `${words.join(' ')} Data`;
}

function mapEmirAssets(
  assets: Array<Record<string, unknown>>,
): EmirAssetLevelTableRow[] {
  return assets.map((asset) => ({
    assetName: String(asset['Asset Name']),
    assetType: String(asset['Asset Type']),
    date: String(asset['Date']),
    emirContribution: Number(asset['EMIR Contribution (EUR)']),
    grossExposureEUR: Number(asset['Gross Exposure (EUR)']),
    isin: String(asset['ISIN']),
    counterparty: asset['Counterparty'] as string | undefined,
  }));
}

function formatFundSectionRow(
  row: EmirFundRow,
  section: EmirFundSectionMetrics,
  index: number,
): Omit<EmirFundLevelTableRow, 'assets'> {
  return {
    fundId: String(row.fund_name),
    fundName: String(row.fund_name_full),
    limit: addCommasToNumbers(section.limit),
    value: addCommasToNumbersAndRound(section.value),
    percentageValue: percentageToTwoDecimalPlaces(section.percentage_value),
    status: section.status,
    index: index + 1,
  };
}

export function buildEmirFundLevelTableData(
  funds: EmirFundRow[],
  selectedKey: string,
  options?: { includeAssets?: boolean },
): EmirFundLevelTableRow[] {
  const includeAssets = options?.includeAssets ?? false;
  const rows: EmirFundLevelTableRow[] = [];

  funds.forEach((row, index) => {
    const section = row[selectedKey];
    if (!section || typeof section === 'string') {
      return;
    }

    const base = formatFundSectionRow(row, section, index);
    if (includeAssets) {
      rows.push({
        ...base,
        assets: mapEmirAssets(section.assets ?? []),
      });
    } else {
      rows.push(base);
    }
  });

  return rows;
}

export function buildEmirMancoLevelTableData(
  payload: EmirStandardBreakdownPayload,
  selectedKey: string,
): EmirMancoLevelTableRow[] {
  const mancoData = payload.manco[selectedKey] as
    | {
        limit: number;
        value: number;
        percentage_value: number;
        status: string;
      }
    | undefined;
  if (!mancoData) {
    return [];
  }

  const mancoName = (payload.manco.manco_name as string | undefined) ?? '';

  return [
    {
      limit: addCommasToNumbers(mancoData.limit),
      value: addCommasToNumbersAndRound(mancoData.value),
      percentageValue: percentageToTwoDecimalPlaces(mancoData.percentage_value),
      status: mancoData.status,
      mancoName,
    },
  ];
}

export const EMIR_TABLE_OPTIONS = {
  paging: false,
  search: false,
  exportButton: true,
};
